/**
 * דף הבית — TASKS 4.1 · PRD 8.1.
 *
 * ⭐ **Hero משולש — Feedback1.md סעיף 6 (20.08.2026).** מבנה דו-טורי ישן
 *    (55/45, טקסט+כפתורים מול תצוגת מוצרים) הוחלף בשלוש עמודות שוות
 *    (0.11): ימין = קטלוג · אמצע = "מי אנחנו" מקוצר + קישור לגלריה (0.12) ·
 *    שמאל = `HeroShowcase` הקיים (ללא שינוי פנימי, רק זז לעמודה שלישית).
 *    שם החברה + הטאגליין נשארים שורת כותרת עליונה קבועה מעל שלוש העמודות
 *    (0.6). קומפ הבחירה: `Hero_וריאציות.html` וריאציה A (6.8/6.8א).
 * ⚠️ כפתור המשנה "מחשבון שטח (מ"ר)" הוסר מה-Hero — Feedback1.md סעיף 5 (19.08.2026).
 * ⚠️ **רשת "4 הקטגוריות" הוסרה — Feedback1.md סעיף 6.7 (20.08.2026).** הקטלוג
 *    עבר למבנה שטוח בלי ניווט קטגוריות (סעיף 8) — אין יותר `/catalog/:slug`
 *    לקשר אליו.
 * ⭐ **בלוק "מי אנחנו" מתחת ל-Hero צומצם לפסקה אחת + קישור "עוד בגלריה"
 *    (0.9)** — התוכן המלא (PIR מול Phenolic, תחום העיסוק) עבר ל-`GalleryPage`
 *    יחד עם תוכן "אודות" הקודם, כדי למנוע כפילות תוכן מלאה בין שני דפים.
 * ⛔ **ללא סעיף תקנים** — הוחרג במפורש ב-PRD 8.1, לא לשכפל מ-`GalleryPage`.
 *
 * ⭐ **עיטור דקורטיבי — פידבק לקוח (טל, 20.08.2026 לילה).** טל דחה את העיצוב
 *    שרק עלה לאוויר (הסעיף הזה עצמו) והעביר מוקאפ LLM שיצר מצילום מסך של
 *    האתר: הדר ורצועת ה-Hero בגרדיאנט קרם, כותרת בתוך "סרט" עם פינות
 *    מחודדות, ושלוש העמודות כרטיסים ממוסגרים עם תג-אייקון עגול. אריאנה
 *    אישרה במפורש לחלץ את הסטייל ולממש בערכת הרכיבים הקיימת — ⛔ לא לשנות
 *    תוכן/מבנה שלוש העמודות (סעיף 6 למעלה), רק את העיצוב שלהן. ראו
 *    `index.css` ("עיצוב דקורטיבי") לטוקנים החדשים — כולם `color-mix` מתוך
 *    `--color-copper` הקיים, אפס צבע חדש.
 * ⚠️ **תבנית גלים ברקע רצועת ה-Hero — נוסתה והוסרה (פידבק אריאנה, סבב המשך
 *    אותו לילה): "הרקע האחרון שיצרת... הגלים לא מתאים בכלל."** הרקע חוזר
 *    לשתי השכבות בלבד (תמונת מוצר מוארת + גרדיאנט `.ornate-surface`),
 *    ⛔ בלי תבנית גרפית נוספת עליהן.
 */
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { Buildings, BookOpen, Package } from '@phosphor-icons/react';
import { COMPANY_NAME, COMPANY_TAGLINE } from '../content/businessInfo.js';
import { HERO_BACKGROUND_IMAGE } from '../content/galleryImages.js';
import { HeroShowcase } from '../components/HeroShowcase.js';
import { TintedSection } from '../components/TintedSection.js';

// ⭐ TASKS 6.2 · DESIGN.md 6.2 — כניסת ה-hero: 550ms לכל אלמנט, 120ms stagger
// ביניהם, "תמונה אחרונה" (התצוגה המתחלפת מקבלת את השהייה הגדולה ביותר).
const HERO_EASE = [0.16, 1, 0.3, 1] as const;
const HERO_STAGGER_SECONDS = 0.12;
const HERO_ITEM_SECONDS = 0.55;

/**
 * ⚠️ **סטייה מודעת מהטבלה המילולית של DESIGN.md 6.2** — שם כתוב "טקסט: opacity
 * 0->1, y 20->0" לכל אלמנט בנפרד. כאן ה-opacity נשאר **ברמת ה-container בלבד**
 * (כמו שהיה לפני 6.2, `data-testid="hero-content"`), וכל אלמנט-ילד מקבל רק
 * `y` משלו. הסיבה: אלמנט שדוהה בנפרד (opacity חלקי) הוא בדיוק הבאג האמיתי
 * שכבר תועד ב-TASKS 4.1/`smoke.spec.ts`/`accessibility-widget.spec.ts` —
 * axe מודד ניגודיות באמצע הדהייה ומדווח כשל שווא. `y` הוא transform בלבד,
 * ⛔ לעולם לא משפיע על חישוב ניגודיות של axe, גם באמצע התזוזה — ולכן ה-stagger
 * הוויזואלי המבוקש מתקבל בלי לשחזר את אותה מלכודת.
 */
function heroSlideUp(order: number) {
  return {
    initial: { y: 20 },
    animate: { y: 0 },
    transition: {
      duration: HERO_ITEM_SECONDS,
      delay: order * HERO_STAGGER_SECONDS,
      ease: HERO_EASE,
    },
  };
}

/**
 * ⭐ DESIGN.md 5.2 "למה אנחנו" — "3 עובדות", ⛔ לא 4. שתי העובדות שדיברו על
 *    הלוחות המבודדים (ייצור + בידוד מובנה) אוחדו לעובדה אחת - אותו תוכן
 *    מאושר בדיוק, בלי לאבד מידע ובלי להמציא עובדה חדשה (סעיף 3, "עובדות
 *    אמיתיות בלבד").
 */
const WHY_US_POINTS: ReadonlyArray<{ title: string; body: string }> = [
  {
    title: 'ייצור מלוחות מבודדים',
    body: 'התעלות והאביזרים מיוצרים אצלנו מלוחות PIR ו-Phenolic מיובאים, עם הבידוד מובנה בלוח עצמו - בלי שכבת בידוד נפרדת אחרי ההתקנה.',
  },
  {
    title: 'התאמה לשרטוט',
    body: 'כל תעלה, קופסת ניפוח ומתאם מיוצרים לפי המידות והשרטוט שלך - לא ממדף קבוע.',
  },
  {
    title: 'משקל נמוך והתקנה מהירה',
    body: 'לוחות מבודדים קלים משמעותית ממתכת, וזה מתורגם לזמן התקנה קצר יותר בשטח.',
  },
];

export function HomePage() {
  return (
    <>
      {/* --- Hero משולש --------------------------------------------------------
          ⭐ רקע: תמונת מרכזייה מוארת (6.1א) מוצבת מאחורי עמודת התצוגה
          (שמאל, `insetInlineEnd` = שמאל ב-RTL), מעליה שכבת גוון לבן אחידה
          וחזקה (0.85) - ⛔ לא גרדיאנט כיווני כמו בקומפ - כדי שהניגודיות של
          הטקסט בשלוש העמודות תישאר בטוחה בלי תלות במיקום העמודה מעל
          התמונה (6.6, "לא להסתמך על בהירות התמונה עצמה"). התמונה עצמה
          עדיין נראית כטקסטורה עדינה ברקע. */}
      <section className="relative overflow-hidden" aria-labelledby="hero-heading">
        <div
          aria-hidden="true"
          className="absolute inset-y-0 w-1/2"
          style={{
            insetInlineEnd: 0,
            backgroundImage: `url(${HERO_BACKGROUND_IMAGE.url})`,
            backgroundSize: '70% auto',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            filter: 'brightness(1.6) saturate(0.6)',
            opacity: 0.35,
          }}
        />
        <div
          aria-hidden="true"
          className="ornate-surface absolute inset-0"
          style={{ opacity: 0.9 }}
        />

        {/* ⚠️ עטיפת opacity 0->1 **ברמת הקונטיינר בלבד** (⛔ לא לכל אלמנט
            בנפרד) - אותה מלכודת axe שכבר תועדה ב-TASKS 4.1/`smoke.spec.ts`:
            כשל ניגודיות שווא באמצע דהייה חלקית. `data-testid="hero-content"`
            נשמר כדי ש-`accessibility-widget.spec.ts` (בודק `opacity: 1` לפני
            כל בדיקת axe) ימשיך לעבוד בלי שינוי. */}
        <motion.div
          data-testid="hero-content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="relative mx-auto max-w-6xl px-6 pt-8 pb-16 sm:pt-12 sm:pb-24"
        >
          {/* ⭐ 0.6 — שם החברה + הטאגליין נשארים שורת כותרת עליונה קבועה מעל
              שלוש העמודות, לזיהוי מותג מיידי (above the fold). ⭐ עודכן
              20.08.2026 (פידבק לקוח) — הכותרת יושבת בתוך "סרט" מעוטר
              (`.ornate-banner`), עם יהלומים דקורטיביים בצידיה במסכים
              רחבים דיים, ומפריד קו+יהלום מתחת אליה לפני הטאגליין. */}
          <motion.div {...heroSlideUp(0)} className="mx-auto max-w-2xl text-center">
            <div className="ornate-banner flex items-center justify-center gap-4 px-10 py-5">
              <span className="ornate-diamond hidden sm:inline-block" aria-hidden="true" />
              <h1
                id="hero-heading"
                className="font-bold"
                style={{ fontSize: 'clamp(1.75rem, 3.2vw, 2.5rem)' }}
              >
                {COMPANY_NAME}
              </h1>
              <span className="ornate-diamond hidden sm:inline-block" aria-hidden="true" />
            </div>
            <div className="ornate-divider mt-4" aria-hidden="true">
              <span className="ornate-diamond" />
            </div>
            <p className="text-muted mx-auto mt-3 max-w-xl text-lg">{COMPANY_TAGLINE}</p>
          </motion.div>

          {/* ⭐ 0.11 — שלוש עמודות שוות בערך (33/33/33). ב-RTL: הראשונה
              ב-DOM (קטלוג) מוצגת ימין, האחרונה (תצוגה) משמאל - בדיוק לפי
              הסדר שטל ביקש. 6.3 — מובייל/טאבלט לאורך קורסים לטור אחד באותו
              סדר DOM; `md:` (⛔ לא `sm:`) כדי שהעמודה השלישית (כרטיס תצוגה
              עם יחס-רוחב קבוע) לא תיחנק ברוחב טלפון לרוחב/טאבלט צר. */}
          {/* ⭐ 20.08.2026 (פידבק לקוח) — שלוש העמודות עוברות מטקסט חופשי
              לכרטיסים ממוסגרים (`.ornate-card`) עם תג-אייקון עגול לכל אחת,
              תואם את המוקאפ. תוכן/סדר/קישורי כל עמודה זהים לגמרי לפני
              הסבב הזה — רק העטיפה הוויזואלית השתנתה. */}
          <div className="mt-10 grid items-stretch gap-8 text-center md:grid-cols-3">
            {/* ימין — קטלוג */}
            <motion.div
              {...heroSlideUp(1)}
              className="ornate-card flex flex-col items-center gap-3 p-6"
            >
              <span className="ornate-icon-badge" aria-hidden="true">
                <BookOpen size={28} weight="regular" />
              </span>
              <h2 className="text-lg font-bold">הקטלוג שלנו</h2>
              <p className="text-link text-base leading-relaxed">
                תעלות, קופסאות ניפוח, תריסים ואביזרי חיבור למערכות מיזוג אוויר - מיוצרים מלוחות PIR
                ופנול לפי המידות והשרטוט שסיפקתם, לצד אביזרים משלימים כמו שטוצרים וגמישים.
              </p>
              <Link to="/catalog" className="btn btn-cta mt-auto">
                לצפייה בקטלוג
              </Link>
            </motion.div>

            {/* אמצע — "מי אנחנו" מקוצר + קישור לגלריה (0.12) */}
            <motion.div
              {...heroSlideUp(2)}
              className="ornate-card flex flex-col items-center gap-3 p-6"
            >
              <span className="ornate-icon-badge" aria-hidden="true">
                <Buildings size={28} weight="regular" />
              </span>
              <h2 className="text-lg font-bold">מי אנחנו</h2>
              <p className="text-link text-base leading-relaxed">
                יבואנית ויצרנית פתרונות למיזוג אוויר - מייבאים לוחות PIR ופנול איכותיים, ומייצרים
                מהם תעלות, קופסאות ניפוח ומתאמים בהתאמה אישית לכל פרויקט. הבידוד מובנה בלוח עצמו, כך
                שאין צורך בשכבת בידוד נפרדת אחרי ההתקנה.
              </p>
              <Link to="/gallery" className="btn btn-secondary mt-auto">
                עוד בגלריה
              </Link>
            </motion.div>

            {/* שמאל — תצוגת המוצרים המתחלפת - "תמונה אחרונה" ב-stagger */}
            <motion.div
              {...heroSlideUp(3)}
              className="ornate-card flex flex-col items-center gap-3 p-6"
            >
              <span className="ornate-icon-badge" aria-hidden="true">
                <Package size={28} weight="regular" />
              </span>
              <h2 className="text-lg font-bold">המוצרים שלנו</h2>
              <div className="w-full">
                <HeroShowcase />
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* --- למה אנחנו ------------------------------------------------------------
          ⭐ כיוון עיצוב "ג' - לוחות א-סימטריים", נבחר על ידי אריאנה 16.08.2026.
          ⭐ **שינוי 20.08.2026 (Feedback1.md 0.9/6.7):** בלוק "מי אנחנו" העצמאי
          שישב כאן בעבר (פסקה + כרטיס לבן) הוסר - הוא כפל בדיוק את התוכן
          שכבר מופיע רגע קודם בעמודה האמצעית של ה-Hero (6.1, וריאציה A
          המאושרת), ושתי הופעות של אותה פסקה במסך גלילה אחד היו כפילות
          תוכן ממשית, ⛔ לא "פשרה" כמו שהוחלט. הרצועה הצבועה (`TintedSection`)
          - שנדרשת להישאר בכל דף ראשי לפי העיצוב - עברה לכאן, ל"למה אנחנו",
          שלא היה לה קודם עיצוב ייחודי משלה. */}
      <TintedSection>
        <section className="px-6 py-14" aria-labelledby="why-us-heading">
          <motion.div
            initial={{ opacity: 0, y: 24, filter: 'blur(6px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            style={{ boxShadow: 'var(--shadow-card)' }}
            className="bg-canvas border-line mx-auto max-w-3xl rounded-lg border p-6 sm:p-10"
          >
            <h2 id="why-us-heading" className="text-center text-3xl font-bold">
              למה אנחנו
            </h2>

            {/* ⭐ נוסף — פידבק אריאנה: יש הרבה מקום פנוי ברצועה הזו, הטקסט
                הוגדל משמעותית (כותרת+גוף) בכל שלוש העובדות. */}
            <dl className="mt-8 flex flex-col sm:flex-row">
              {WHY_US_POINTS.map((point, index) => (
                <div
                  key={point.title}
                  className={`flex-1 px-6 py-5 text-center ${
                    index > 0 ? 'border-line border-t sm:border-s sm:border-t-0' : ''
                  }`}
                >
                  <dt className="text-xl font-bold">{point.title}</dt>
                  <dd className="text-ink mt-3 text-lg leading-relaxed">{point.body}</dd>
                </div>
              ))}
            </dl>
          </motion.div>
        </section>
      </TintedSection>

      {/* --- CTA מסכם ----------------------------------------------------------- */}
      {/* ⚠️ **עודכן 20.08.2026 (פידבק אריאנה) — הופך את DESIGN.md 5.2.** הכפתור
          היה "בקשת הצעת מחיר" ל-`/cart`, אותו נוסח כמו ה-Header. תוקן: מבקר
          ראשון בדף הבית עדיין לא ביקר בקטלוג, ולכן עגלת הבקשות שלו ריקה -
          כפתור ל-`/cart` באותו רגע לא מוביל לשום מקום שימושי. הכפתור מוביל
          עכשיו לקטלוג עצמו, אותו נוסח בדיוק כמו כרטיס "הקטלוג שלנו" למעלה
          (עקביות טקסט, ⛔ לא ניסוח שלישי). */}
      <section className="mx-auto max-w-6xl px-6 py-16 text-center" aria-labelledby="cta-heading">
        <h2 id="cta-heading" className="text-3xl font-bold">
          מוכנים להתחיל?
        </h2>
        {/* ⭐ נוסף — פידבק אריאנה: פחות מקום פנוי מ"למה אנחנו", אז הגדלה
            מתונה יותר, אבל עם הבלטה - טקסט כהה (⛔ לא muted) ומודגש. */}
        <p className="text-ink mx-auto mt-3 max-w-md text-lg font-medium">
          בחרו מוצרים מהקטלוג והוסיפו אותם לעגלת הבקשות - נשלח לכם הצעת מחיר בהתאמה אישית.
        </p>
        <div className="mt-6">
          <Link to="/catalog" className="btn btn-cta">
            לצפייה בקטלוג
          </Link>
        </div>
      </section>
    </>
  );
}
